import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Routes, Route, Link } from 'react-router-dom';
import AboutUs from './pages/AboutUs';
import CircularText from './components/CircularText';
import LanguageToggle from './components/ui/LanguageToggle';
// import { useTranslation } from 'react-i18next';

// Add this TypeScript declaration to fix linter error for window.confetti
declare global {
  interface Window {
    confetti?: (options: any) => void;
  }
}

// --- Barcode SVG ---
function Barcode({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 180 40" className={className} fill="none" aria-hidden="true">
      <rect x="0" y="0" width="180" height="40" fill="white" />
      <g stroke="black" strokeWidth="3">
        <line x1="10" y1="5" x2="10" y2="35" />
        <line x1="18" y1="5" x2="18" y2="35" />
        <line x1="26" y1="5" x2="26" y2="35" />
        <line x1="34" y1="5" x2="34" y2="35" />
        <line x1="44" y1="5" x2="44" y2="35" />
        <line x1="52" y1="5" x2="52" y2="35" />
        <line x1="60" y1="5" x2="60" y2="35" />
        <line x1="70" y1="5" x2="70" y2="35" />
        <line x1="80" y1="5" x2="80" y2="35" />
        <line x1="90" y1="5" x2="90" y2="35" />
        <line x1="100" y1="5" x2="100" y2="35" />
        <line x1="110" y1="5" x2="110" y2="35" />
        <line x1="120" y1="5" x2="120" y2="35" />
        <line x1="130" y1="5" x2="130" y2="35" />
        <line x1="140" y1="5" x2="140" y2="35" />
        <line x1="150" y1="5" x2="150" y2="35" />
        <line x1="160" y1="5" x2="160" y2="35" />
        <line x1="170" y1="5" x2="170" y2="35" />
      </g>
    </svg>
  );
}

// --- Large Stamp ---
function Stamp({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <motion.div
      className={`inline-block px-6 py-2 border-4 border-black bg-white shadow-xl font-black text-3xl md:text-6xl uppercase tracking-tighter select-none ${className}`}
      style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif', letterSpacing: '-0.06em' }}
    >
      {children}
    </motion.div>
  );
}

// --- Minimal Email Input ---
function WaitlistForm({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Confetti effect on success
  function triggerConfetti() {
    if (window.confetti) {
      window.confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }

  return (
    <form
      className="flex flex-col items-center gap-2 mt-6 w-full max-w-xs mx-auto"
      onSubmit={async e => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        setSuccess(false);
        setError(null);
        const apiUrl = import.meta.env.DEV
          ? '/api/waitlist'
          : 'https://waitlist-worker.zoomiesgp.workers.dev/waitlist';
        try {
          const res = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const data = await res.json();
          if (res.ok && data.success) {
            setSuccess(true);
            setEmail('');
            onSuccess();
            triggerConfetti();
          } else {
            setError(data.error || 'Something went wrong');
          }
        } catch (err) {
          setError('Network error');
        }
        setLoading(false);
      }}
    >
      <input
        type="email"
        required
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="Email address"
        className="bg-white border-2 border-black text-2xl font-extrabold px-4 py-3 w-full focus:outline-none placeholder:text-black/40 rounded-lg shadow-md"
        style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}
        disabled={loading || success}
      />
      <motion.button
        type="submit"
        className="font-black text-xl px-4 py-2 bg-black text-white uppercase tracking-widest w-full mt-2 cta-glow"
        style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}
        disabled={loading || success}
        whileHover={{ scale: 1.05 }}
      >
        {loading ? '...' : 'JOIN THE WAITLIST'}
      </motion.button>
      {success && (
        <span className="block w-full text-center text-green-600 font-bold mt-2 animate-pulse">Thank you for joining! See you soon in the Zoomies & Co community!</span>
      )}
      {error && (
        <span className="block w-full text-center text-red-600 font-bold mt-2 animate-pulse">{error}</span>
      )}
      {/* Privacy note */}
      <div className="mt-2 text-xs text-black/50 text-center max-w-xs" aria-live="polite">
        We respect your privacy. No spam, ever.
      </div>
    </form>
  );
}

const perkCardVariants = {
  rest: { zIndex: 1 },
  hover: { zIndex: 2 },
};
const perkImgVariants = {
  rest: { opacity: 0, scale: 0.95 },
  hover: { opacity: 0.8, scale: 1.1 },
};

// --- Homepage Component ---
function Homepage() {
  // const { t } = useTranslation();
  return (
    <div className="min-h-screen w-full bg-white text-black font-sans overflow-x-hidden">
      {/* Language Toggle Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageToggle />
      </div>
      {/* HERO BLOCK */}
      <section className="relative w-full min-h-[80vh] flex flex-col justify-center items-center px-4 py-12 border-b-4 border-black overflow-hidden">
        {/* Barcode top left */}
        <motion.div initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="absolute top-4 left-2 z-10">
          <Barcode className="w-32 h-8" />
        </motion.div>
        {/* Circular Instagram Text - bottom right, always visible on desktop */}
        <div className="fixed bottom-8 right-8 z-50">
          <a href="https://www.instagram.com/zoomiesandco.official/" target="_blank" rel="noopener noreferrer">
            <CircularText text="Follow us on Instagram • " spinDuration={18} onHover="speedUp" />
          </a>
        </div>
        {/* Giant Logo Stamp */}
        <div className="flex flex-col items-center gap-2 z-20">
          <Stamp className="mb-2">ZOOMIES & CO</Stamp>
          <span className="text-xs md:text-lg font-bold tracking-widest uppercase bg-black text-white px-2 py-1 mt-1" style={{ display: 'block' }}>SWITZERLAND, GET READY FOR ZOOMIES</span>
        </div>
        {/* Tagline for clarity */}
        <div className="mt-2 text-center text-base md:text-xl font-semibold text-black/80 max-w-lg mx-auto z-20" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
          The ultimate destination for unique, playful, and high-quality pet essentials. Sign up to get early access and exclusive perks!
        </div>
        {/* Subtitle */}
        <div className="mt-2 text-center text-lg md:text-2xl font-bold uppercase tracking-tight max-w-xl mx-auto z-20" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
          Where every pet's story begins with
          {' '}
          <span className="text-pink-500">LOVE</span>
        </div>
        {/* Waitlist Form */}
        <div className="mt-6 z-20 w-full max-w-xs flex flex-col items-center">
          {/* Animated Mascot */}
          <div className="mb-2 animate-bounce flex items-center justify-center">
            <img src="/avatar.png" alt="Mascot" className="w-16 h-16 md:w-24 md:h-24 rounded-full object-cover shadow-lg border-2 border-black" />
          </div>
          <div className="w-full bg-white/90 border-2 border-black rounded-xl shadow-xl p-4 flex flex-col items-center">
            <WaitlistForm onSuccess={() => {}} />
            {/* Social proof */}
            <div className="mt-3 text-sm md:text-base text-black/60 font-medium text-center" aria-live="polite">
              Join 1,200+ other pet lovers on the waitlist!
            </div>
          </div>
        </div>
        {/* Shipping Perks */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 z-20">
          <div className="px-4 py-2 bg-black text-white font-extrabold text-base md:text-lg uppercase tracking-widest border-4 border-black shadow-xl rounded rotate-[-2deg] select-none" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
            WE SHIP NATIONWIDE
          </div>
          <div className="px-4 py-2 bg-black text-white font-bold text-base md:text-lg uppercase tracking-widest border-4 border-black shadow-xl rounded rotate-2 select-none" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
            FREE SHIPPING ON ORDERS CHF 50+
          </div>
        </div>
        {/* Overlapping barcode bottom right */}
        <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="absolute bottom-4 right-2 z-10">
          <Barcode className="w-32 h-8" />
        </motion.div>
        {/* Manga pet illustration placeholder */}
        <div className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 z-0 opacity-10 pointer-events-none select-none">
          <div className="w-40 h-40 md:w-64 md:h-64 border-4 border-black bg-white flex items-center justify-center rounded-full">
            <span className="text-7xl md:text-9xl" aria-label="paw" role="img">🐾</span>
            {/* Placeholder: Replace with manga-style pet art */}
          </div>
        </div>
      </section>

      {/* VISUAL SECTION */}
      <section className="w-full grid grid-cols-1 md:grid-cols-2 gap-0 border-b-4 border-black min-h-[320px]">
        {/* Left: Manga pet illustration placeholder */}
        <div className="flex items-center justify-center bg-white min-h-[200px] border-b-4 md:border-b-0 md:border-r-4 border-black relative">
          <div className="w-32 h-32 md:w-56 md:h-56 border-4 border-black bg-white flex items-center justify-center rounded-full overflow-hidden">
            <img src="/paws.png" alt="Manga-style pet paws" className="object-cover w-full h-full grayscale contrast-200" />
          </div>
          <span className="absolute bottom-2 right-2 text-xs font-bold text-black/40">Manga</span>
        </div>
        {/* Right: Text block with grunge effect */}
        <div className="flex flex-col items-center justify-center p-6 md:p-12 relative overflow-visible">
          <div className="relative w-full flex flex-col items-center">
            <motion.div
              whileHover={{ x: [0, 8, -8, 0], rotate: [0, 2, -2, 0] }}
              className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none select-none mix-blend-multiply"
              style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif', letterSpacing: '-0.04em' }}
            >
              PAWS / FANGS / LOVE
            </motion.div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20 text-7xl md:text-9xl font-black select-none pointer-events-none rotate-6 whitespace-nowrap">
              PAWS / FANGS / LOVE
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS ZOOMIES & CO SECTION */}
      <section className="w-full py-16 px-4 border-b-4 border-black bg-white flex flex-col items-center">
        <div className="max-w-4xl w-full">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              What is Zoomies & Co?
            </h2>
            <div className="w-24 h-1 bg-black mx-auto"></div>
          </div>
          
          {/* Learn About Us Button */}
          <div className="text-center">
            <Link 
              to="/about"
              className="inline-block bg-black text-white px-8 py-4 border-4 border-black hover:bg-white hover:text-black transition-all duration-300 font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}
            >
              Learn About Us
            </Link>
          </div>
        </div>
      </section>

      {/* TAKE A PEEK AT OUR COLLECTION SECTION */}
      <section className="w-full py-16 px-4 border-b-4 border-black bg-gray-50 flex flex-col items-center">
        <div className="max-w-4xl w-full">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              Where the Zoomies Begin.
            </h2>
            <div className="w-24 h-1 bg-black mx-auto mb-4"></div>
            <p className="text-lg md:text-xl font-bold text-black/80" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
              Only The Good Stuff
            </p>
            <p className="text-base md:text-lg text-black/60 italic" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
              Tested by pets, approved by picky humans (that's us)
            </p>
          </div>
          
          {/* Show Me Button */}
          <div className="text-center">
            <button
              className="inline-block bg-black text-white px-8 py-4 border-4 border-black hover:bg-white hover:text-black transition-all duration-300 font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transform hover:-translate-y-1 cursor-default"
              style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}
              disabled
            >
              Stay Tuned
            </button>
          </div>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="w-full py-10 px-4 border-b-4 border-black bg-white flex flex-col items-center">
        <div className="max-w-2xl w-full grid grid-cols-1 md:grid-cols-6 gap-2 md:gap-6 items-end">
          <div className="md:col-span-4 text-2xl md:text-4xl font-extrabold leading-tight uppercase" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
            The structural adaptation of
            <span className="font-normal">care</span>
            enables pets to
            <span className="font-normal">thrive</span>.
          </div>
          <div className="md:col-span-2 flex flex-col gap-2 mt-4 md:mt-0">
            <span className="text-base md:text-lg font-bold uppercase tracking-widest">Power to Live</span>
            <span className="text-xs md:text-sm font-normal">(The power to live)</span>
          </div>
        </div>
        <div className="mt-4 w-full text-center text-lg md:text-xl font-normal max-w-xl mx-auto">
          We believe in the biology of love, the science of play, and the poetry of paws. Zoomies & Co is for pets who are more than cute—they're wild, weird, and worthy.
        </div>
      </section>

      {/* WAITLIST PERKS */}
      <section className="w-full py-10 px-4 border-b-4 border-black bg-white flex flex-col items-center">
        <div className="flex flex-col md:flex-row gap-6 w-full max-w-3xl justify-center items-stretch">
          {/* Perk 1 */}
          <motion.div
            whileHover="hover"
            initial="rest"
            animate="rest"
            variants={perkCardVariants}
            className="flex-1 bg-black text-white border-4 border-black flex flex-col justify-center items-center px-4 py-8 min-h-[120px] relative overflow-visible"
          >
            <motion.img
              src="/pop.jpeg"
              alt="Perk pop image"
              className="absolute inset-0 w-full h-full object-cover rounded-lg z-0 pointer-events-none transition-all duration-300"
              variants={perkImgVariants}
              style={{ filter: 'blur(2px)' }}
            />
            <span className="absolute left-2 top-2 text-xs font-bold rotate-90 tracking-widest z-10">Early Access</span>
            <span className="text-2xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-2 relative z-10" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              <span role="img" aria-label="rocket" className="transition-transform group-hover:scale-125">🚀</span> Early Access
            </span>
          </motion.div>
          {/* Perk 2 */}
          <motion.div
            whileHover="hover"
            initial="rest"
            animate="rest"
            variants={perkCardVariants}
            className="flex-1 bg-black text-white border-4 border-black flex flex-col justify-center items-center px-4 py-8 min-h-[120px] relative overflow-visible"
          >
            <motion.img
              src="/pop.jpeg"
              alt="Perk pop image"
              className="absolute inset-0 w-full h-full object-cover rounded-lg z-0 pointer-events-none transition-all duration-300"
              variants={perkImgVariants}
              style={{ filter: 'blur(2px)' }}
            />
            <span className="absolute right-2 top-2 text-xs font-bold -rotate-90 tracking-widest z-10">Discount</span>
            <span className="text-2xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-2 relative z-10" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              <span role="img" aria-label="money" className="transition-transform group-hover:scale-125">💸</span> Launch Discounts
            </span>
          </motion.div>
          {/* Perk 3 */}
          <motion.div
            whileHover="hover"
            initial="rest"
            animate="rest"
            variants={perkCardVariants}
            className="flex-1 bg-black text-white border-4 border-black flex flex-col justify-center items-center px-4 py-8 min-h-[120px] relative overflow-visible"
          >
            <motion.img
              src="/pop.jpeg"
              alt="Perk pop image"
              className="absolute inset-0 w-full h-full object-cover rounded-lg z-0 pointer-events-none transition-all duration-300"
              variants={perkImgVariants}
              style={{ filter: 'blur(2px)' }}
            />
            <span className="absolute left-2 bottom-2 text-xs font-bold rotate-180 tracking-widest z-10">Reward?</span>
            <span className="text-xl md:text-2xl font-black uppercase tracking-tight text-center flex items-center gap-2 relative z-10" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              <span role="img" aria-label="paw" className="transition-transform group-hover:scale-125">🐾</span> Your pet shall thank you (& judge you less)
            </span>
          </motion.div>
        </div>
      </section>

      {/* Bottom Sign-Up Box */}
      <section className="w-full flex flex-col items-center justify-center py-10 px-4 bg-white border-b-4 border-black">
        <div className="w-full max-w-xs bg-white/90 border-2 border-black rounded-xl shadow-xl p-6 flex flex-col items-center">
          <div className="mb-2 text-xl md:text-2xl font-extrabold text-center">Don't miss out! Sign up for early access:</div>
          <WaitlistForm onSuccess={() => {}} />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="w-full py-6 px-4 grid grid-cols-1 md:grid-cols-3 items-center bg-white border-t-4 border-black gap-4 text-xs md:text-sm">
        <div className="flex items-center gap-4 justify-center md:justify-start">
          {/* Social icons (black only, SVGs) */}
          <a href="https://www.instagram.com/zoomiesandco.official/" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform hover:opacity-70" aria-label="Instagram">
            <svg width="24" height="24" fill="none" stroke="black" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17" cy="7" r="1.5"/></svg>
          </a>
        </div>
        <div className="flex flex-col items-center justify-center text-center font-bold text-lg md:text-xl tracking-widest mx-2">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl md:text-3xl" aria-label="paw" role="img">🐾</span>
            <span className="text-2xl md:text-3xl" aria-label="dog" role="img">🐶</span>
          </div>
          <span className="mt-1">Spreading joy, one wag at a time</span>
          <div className="flex flex-col md:flex-row gap-2 mt-2">
            <Link to="/privacy-policy" className="hover:underline hover:opacity-70">Privacy Policy</Link>
            <Link to="/terms" className="hover:underline hover:opacity-70">Terms & Conditions</Link>
            <Link to="/impressum" className="hover:underline hover:opacity-70">Impressum</Link>
          </div>
        </div>
        <div className="flex gap-4 justify-center md:justify-end">
          {/* Removed old Contact/Email/Terms links */}
        </div>
      </footer>
    </div>
  );
}

function PrivacyPolicy() {
  // const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen w-full flex flex-col items-center py-16 px-4">
      <div className="max-w-3xl w-full">
        <h1 className="text-4xl font-black mb-8 text-center">Privacy on the Move: Zoomies & Co.'s Commitment to Your Data Protection</h1>
        <p className="mb-6">This Privacy Policy outlines how Zoomies & Co. ("we," "us," or "our") collects, uses, and protects your personal information when you visit our website, use our services, or interact with us. We are committed to ensuring your privacy is protected in compliance with the Swiss Federal Act on Data Protection (rev-FADP) and the General Data Protection Regulation (GDPR).</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">1. Who We Are and How to Contact Us</h2>
        <p className="mb-4">At Zoomies & Co, we believe pets deserve the best—because they are the best. We're a pet wellness brand built with love, care, and a little bit of obsession. From joyful tails to cozy naps, every moment with your pet is precious—and we're here to make it even better.</p>
        <p className="mb-4">We're currently preparing to bring our thoughtfully curated pet products to Switzerland, and this waitlist is just the beginning of something wonderful.</p>
        <p className="mb-4">If you have any questions about this Privacy Policy or our data protection practices, you can contact us at:</p>
        <ul className="mb-6 list-disc list-inside">
          <li>Zoomies & Co.</li>
          <li>hello@thezoomiesco.ch</li>
          <li>1621 Central Ave #8942<br/>Cheyenne, WY 82001.</li>
        </ul>
        <h2 className="text-2xl font-bold mt-8 mb-2">2. What Personal Data We Collect and Why</h2>
        <p className="mb-4">We collect various types of personal data to provide and improve our services, process your orders, and communicate with you. The types of data we collect and the purposes for which we process it are detailed below.</p>
        <table className="mb-6 w-full border text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-2 py-1">Purpose of Processing</th>
              <th className="border px-2 py-1">Types of Personal Data Collected</th>
              <th className="border px-2 py-1">Legal Basis for Processing (under GDPR)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border px-2 py-1">Waitlist Notification</td>
              <td className="border px-2 py-1">Email Address</td>
              <td className="border px-2 py-1">Consent. You provide consent by entering your email on our waitlist form. We will use this only to notify you when Zoomies & Co. officially goes live.</td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Weekly Newsletter</td>
              <td className="border px-2 py-1">Email Address</td>
              <td className="border px-2 py-1">Consent. You provide consent by opting into our newsletter. We will use this to send you weekly updates, news, and promotions.</td>
            </tr>
          </tbody>
        </table>
        <h2 className="text-2xl font-bold mt-8 mb-2">3. How We Use Cookies and Tracking Technologies</h2>
        <p className="mb-4">We use cookies and similar tracking technologies to enhance your Browse experience, analyze website traffic, and for marketing purposes. You have the right to accept or refuse cookies. You can manage your cookie preferences through our cookie consent banner and your browser settings. For more detailed information, please see our [Link to a separate Cookie Policy, if applicable].</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">4. Who We Share Your Personal Data With</h2>
        <p className="mb-4">We do not sell your personal data to third parties. We may share your data with the following categories of recipients to provide our services:</p>
        <ul className="mb-6 list-disc list-inside">
          <li>Payment Processors: To securely process your payments.</li>
          <li>Shipping and Fulfillment Partners: To deliver your orders.</li>
          <li>Legal and Regulatory Authorities: When required by law or to protect our legal rights.</li>
        </ul>
        <h2 className="text-2xl font-bold mt-8 mb-2">5. International Data Transfers</h2>
        <p className="mb-4">Your personal data may be transferred to and processed in countries outside of Switzerland and the European Economic Area (EEA). When we do so, we ensure that appropriate safeguards are in place to protect your data, such as using Standard Contractual Clauses approved by the European Commission and ensuring the recipient country has an adequate level of data protection as recognized by the Swiss Federal Data Protection and Information Commissioner (FDPIC) and the European Commission.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">6. How Long We Keep Your Data</h2>
        <p className="mb-4">We will only retain your personal data for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.</p>
        <ul className="mb-6 list-disc list-inside">
          <li>Order Information: Retained to comply with tax and commercial laws.</li>
          <li>Account Information: Retained for as long as your account is active.</li>
          <li>Marketing Consents: Retained until you withdraw your consent.</li>
        </ul>
        <h2 className="text-2xl font-bold mt-8 mb-2">7. Your Data Protection Rights</h2>
        <p className="mb-4">Under the rev-FADP and GDPR, you have the following rights regarding your personal data:</p>
        <ul className="mb-6 list-disc list-inside">
          <li><b>Right of Access:</b> You have the right to request a copy of the personal data we hold about you.</li>
          <li><b>Right to Rectification:</b> You have the right to request that we correct any inaccurate or incomplete data.</li>
          <li><b>Right to Erasure ('Right to be Forgotten'):</b> You have the right to request that we delete your personal data under certain conditions.</li>
          <li><b>Right to Restrict Processing:</b> You have the right to request that we restrict the processing of your personal data under certain conditions.</li>
          <li><b>Right to Data Portability:</b> You have the right to receive your personal data in a structured, commonly used, and machine-readable format and to transmit that data to another controller.</li>
          <li><b>Right to Object:</b> You have the right to object to the processing of your personal data, particularly for direct marketing purposes.</li>
          <li><b>Right to Withdraw Consent:</b> Where we rely on your consent to process your data, you have the right to withdraw that consent at any time.</li>
          <li><b>Right to Lodge a Complaint:</b> You have the right to lodge a complaint with a supervisory authority. In Switzerland, this is the Federal Data Protection and Information Commissioner (FDPIC). You can also lodge a complaint with the supervisory authority in the EU member state of your habitual residence, place of work, or where the alleged infringement took place.</li>
        </ul>
        <p className="mb-4">To exercise any of these rights, please contact us using the details provided in Section 1.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">8. Data Security</h2>
        <p className="mb-4">We have implemented appropriate technical and organizational security measures to protect your personal data from accidental or unlawful destruction, loss, alteration, unauthorized disclosure, or access.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">9. Changes to This Privacy Policy</h2>
        <p className="mb-4">We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top.</p>
        <p className="mb-4">This Privacy Policy has been tailored to be compliant with both the Swiss rev-FADP and the GDPR. It is important to review and adapt the bracketed and bolded information to accurately reflect the specific practices of Zoomies & Co. It is also recommended to have the final document reviewed by a legal professional specializing in data protection law.</p>
      </div>
    </div>
  );
}
function Terms() {
  // const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen w-full flex flex-col items-center py-16 px-4">
      <div className="max-w-3xl w-full">
        <h1 className="text-4xl font-black mb-8 text-center">Terms & Conditions for Zoomies & Co.</h1>
        <p className="mb-6">Welcome to Zoomies & Co. ("we," "us," "our," or "Company"). These Terms & Conditions ("Terms") govern your access to and use of our website, located at thezoomiesco.ch, and any other services we provide, including our waitlist (collectively, the "Services").</p>
        <p className="mb-6">By accessing our website or signing up for our waitlist, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use our Services.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">1. Our Services & The Waitlist</h2>
        <p className="mb-4">Zoomies & Co. is currently in a pre-launch phase. Our website provides information about us and allows you to sign up for a waitlist.</p>
        <p className="mb-4"><b>Joining the Waitlist:</b> By submitting your email address, you agree to be placed on our waitlist. This allows us to notify you of our official launch, provide you with updates, and potentially offer early access or special promotions.</p>
        <p className="mb-4"><b>No Obligation:</b> Joining the waitlist does not create a contract for future services or guarantee you will be able to purchase our products. It is an expression of interest only. We do not guarantee a specific launch date or the availability of any product.</p>
        <p className="mb-4"><b>Communication:</b> By joining the waitlist, you consent to receive electronic communications from us related to the launch and our newsletter, as detailed in our Privacy Policy. You may unsubscribe at any time by clicking the "unsubscribe" link in our emails.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">2. Intellectual Property Rights</h2>
        <p className="mb-4">All content, branding, designs, and technology related to our Services are the exclusive property of Zoomies & Co. and its licensors.</p>
        <p className="mb-4"><b>Ownership:</b> The Zoomies & Co. name, logo, slogans, product names, website text, graphics, and all other content on our website are our trademarks and copyrighted material. All rights are reserved.</p>
        <p className="mb-4"><b>Limited License:</b> We grant you a limited, non-exclusive, non-transferable, and revocable license to access and use our Services for your personal, non-commercial use only.</p>
        <p className="mb-4"><b>Restrictions:</b> You may not copy, reproduce, modify, distribute, display, create derivative works of, or otherwise exploit any part of our intellectual property without our express prior written consent.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">3. Disclaimers</h2>
        <p className="mb-4">Our Services are provided on an "as is" and "as available" basis.</p>
        <p className="mb-4"><b>No Warranties:</b> We make no warranties or representations of any kind, whether express or implied, regarding the operation of our Services or the information, content, or materials included on our website. To the fullest extent permissible by law, we disclaim all warranties, including but not limited to, implied warranties of merchantability and fitness for a particular purpose.</p>
        <p className="mb-4"><b>Forward-Looking Statements:</b> Our website may contain "forward-looking statements" about our launch, products, and future plans. These statements are based on our current expectations and are not guarantees of future performance. Actual results may differ materially.</p>
        <p className="mb-4"><b>Accuracy of Information:</b> While we strive to provide accurate information, we do not warrant that product descriptions, launch dates, or other content on the site are accurate, complete, reliable, current, or error-free.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">4. Limitation of Liability</h2>
        <p className="mb-4">To the fullest extent permitted by applicable law, Zoomies & Co., its affiliates, officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from:</p>
        <ul className="mb-6 list-disc list-inside">
          <li>your access to or use of, or inability to access or use, the Services;</li>
          <li>any conduct or content of any third party on the Services;</li>
          <li>unauthorized access, use, or alteration of your transmissions or content.</li>
        </ul>
        <p className="mb-4">Our total liability for any claim arising out of these Terms or your use of the Services will not exceed the amount of fifty U.S. dollars ($50.00).</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">5. User Conduct</h2>
        <p className="mb-4">You agree not to use the Services for any unlawful purpose or in any way that could harm, disable, overburden, or impair our servers or networks. You agree not to attempt to gain unauthorized access to any part of the Services or to any of our systems.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">6. Governing Law</h2>
        <p className="mb-4">These Terms and any dispute or claim arising out of or in connection with them shall be governed by and construed in accordance with the laws of the State of Wyoming, USA, without regard to its conflict of law provisions.</p>
        <p className="mb-4">You agree that any legal action or proceeding arising out of or related to these Terms shall be brought exclusively in the state or federal courts located in Wyoming, and you hereby consent to the jurisdiction of such courts.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">7. Changes to These Terms</h2>
        <p className="mb-4">We reserve the right to modify these Terms at any time. We will notify you of any changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of the Services after any such changes constitutes your acceptance of the new Terms.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">8. Contact Us</h2>
        <p className="mb-4">If you have any questions about these Terms, please contact us at:</p>
        <ul className="mb-6 list-disc list-inside">
          <li>Zoomies & Co.</li>
          <li>hello@thezoomiesco.ch</li>
          <li>1621 Central Ave #8942 , Cheyenne, WY 82001</li>
        </ul>
      </div>
    </div>
  );
}
function Impressum() {
  // const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen w-full flex flex-col items-center py-16 px-4">
      <div className="max-w-3xl w-full">
        <h1 className="text-4xl font-black mb-8 text-center">Impressum / Legal Notice</h1>
        <p className="mb-6">Information provided in accordance with German and Swiss law (§ 5 TMG & Art. 3 UWG).</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">Responsible for the content of this website:</h2>
        <ul className="mb-6 list-disc list-inside">
          <li>Zoomies & Co</li>
          <li>Represented by: Zoomies Pets LLC</li>
          <li>1621 Central Ave # 8942</li>
          <li>Cheyenne 82001</li>
          <li>Wyoming, USA</li>
        </ul>
        <h2 className="text-2xl font-bold mt-8 mb-2">Contact:</h2>
        <ul className="mb-6 list-disc list-inside">
          <li>Email: hello@thezoomiesco.ch</li>
          <li>Website: thezoomiesco.ch</li>
        </ul>
        <h2 className="text-2xl font-bold mt-8 mb-2">Company Registration Information:</h2>
        <ul className="mb-6 list-disc list-inside">
          <li>Registered at: Wyoming Secretary of State</li>
          <li>Registration Number / Filing ID: 2025-001696503</li>
        </ul>
        <h2 className="text-2xl font-bold mt-8 mb-2">VAT Identification Number (VAT ID):</h2>
        <p className="mb-6">As a US-based company, we are not currently registered for VAT in the EU or Switzerland.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">Online Dispute Resolution (ODR):</h2>
        <p className="mb-6">The European Commission provides a platform for online dispute resolution (ODR), which is accessible at: <a href="http://ec.europa.eu/consumers/odr/" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">http://ec.europa.eu/consumers/odr/</a>.<br/>We are not obligated and not willing to participate in a dispute settlement procedure before a consumer arbitration board.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">Liability Disclaimer:</h2>
        <p className="mb-4">While we carefully control the content of our website, we do not assume any liability for the content of external links. The operators of the linked pages are solely responsible for their content. All content on this website is subject to our Terms & Conditions.</p>
      </div>
    </div>
  );
}

// --- Main App Component with Routing ---
export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Homepage />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/impressum" element={<Impressum />} />
    </Routes>
  );
}
